import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import FullPageTransition from 'app/home/general-components/FullPageTransition';
import { AddRemoteCompleted, AddRemoteError, AddRemoteSelectBrand, AddRemoteSelectDeviceType, AddRemoteTestCommand } from './Pages';
import { CurrentPageContext, useHistoryState } from 'app/home/general-components/HistoryStackContext';
import { v4 as uuidv4 } from 'uuid';

import './AddRemote.scss';

export default function AddRemote({ addRemoteController, devices }) {
    const p = useContext(CurrentPageContext);

    const parentDeviceMac = p.state.parentDeviceMac;

    const subDevices = useMemo(() => {
        let parentDevice = devices.find(({ mac }) => mac === parentDeviceMac);
        return parentDevice.sub_devices;
    }, [devices, parentDeviceMac]);

    const { delayedCurrentState, pushState, back, setLockState, numberOfStates } =
        useHistoryState(p.id, {
            stage: 'select-type'
        });
    const currentState = delayedCurrentState;

    const stage = currentState.stage;
    const setStage = useCallback((v) => {
        pushState({
            stage: v
        });
    }, [pushState]);

    const dismissHandler = useCallback(() => {
        setLockState(true);
        setTimeout(() => {
            back(true);
            setTimeout(() => { // fix a bug in safari: two consecutive backs will result in only perfoming the second one
                p.dismiss();
            });
        });
    }, [back, p, setLockState]);

    const [{ inverse }, setlastnNumberOfStates] = useState({ inverse: false, lastNumberOfStates: 0 });
    useEffect(() => {
        // console.log("numberOfStates", numberOfStates);
        setlastnNumberOfStates((x) => {
            return { inverse: numberOfStates < x.lastNumberOfStates, lastNumberOfStates: numberOfStates }
        })
    }, [numberOfStates]);

    const [supportedSubDeviceForDevice, setSupportedSubDeviceForDevice] = useState(undefined);
    const [selectedSubType, setSelectedSubType] = useState(undefined);
    const [supportedBrands, setSupportedBrands] = useState(undefined);
    const [selectedBrand, setSelectedBrand] = useState(undefined);
    const [supportedIndices, setSupportedIndices] = useState(undefined);
    const [selectedIndex, setSelectedIndex] = useState(undefined);
    const [newRemoteMac, setNewRemoteMac] = useState(undefined);
    const [errorId, setErrorId] = useState(undefined);
    const newRemoteName = useMemo(() => {
        // console.log("newRemoteName CHECK for", newRemoteMac, subDevices);
        for (let i = 0; i < subDevices.length; i++) {
            const remote = subDevices[i];
            if (remote.mac === newRemoteMac) {
                // console.log("newRemoteName FOUND", remote.name);
                return remote.name;
            }
        }
        // console.log("newRemoteName NOT FOUND");
        return undefined;
    }, [subDevices, newRemoteMac]);

    const init = useCallback(() => {
        addRemoteController.getSubTypes(parentDeviceMac);
    }, [addRemoteController, parentDeviceMac]);

    const onTypeSelected = useCallback((type) => {
        setSelectedSubType(type);
        addRemoteController.getBrands(parentDeviceMac, type);
        setStage("select-brand");
    }, [addRemoteController, parentDeviceMac, setStage]);

    const onBrandSelected = useCallback((brandId) => {
        setSelectedBrand(brandId);
        addRemoteController.getIndices(parentDeviceMac, selectedSubType, brandId);
        setStage("test-command");
    }, [addRemoteController, selectedSubType, parentDeviceMac, setStage]);

    const testCommand = useCallback((index, func) => {
        addRemoteController.sendTestCommand(parentDeviceMac, selectedSubType, selectedBrand, index, func);
    }, [addRemoteController, selectedSubType, parentDeviceMac, selectedBrand]);

    const onIndexSelected = useCallback((index) => {
        setSelectedIndex(index);
        addRemoteController.join(parentDeviceMac, selectedSubType, selectedBrand, index);
        setStage("join");
    }, [addRemoteController, parentDeviceMac, selectedSubType, selectedBrand, setStage]);

    const renameRemote = useCallback((newName) => {
        const devicesWithThisMac = devices.filter(device => device.mac === parentDeviceMac);
        if (devicesWithThisMac.length > 0) {
            const subdevicesWithThisMac = devicesWithThisMac[0].sub_devices.filter(subdevice => subdevice.mac === newRemoteMac);
            if (subdevicesWithThisMac.length > 0) {
                subdevicesWithThisMac[0].rename(newName);
                // dismiss();
            }
        }
        dismissHandler();
    }, [dismissHandler, parentDeviceMac, newRemoteMac, devices]);

    const onRetry = useCallback(() => {
        setStage('select-type');
        setSupportedSubDeviceForDevice(undefined);
        setSelectedSubType(undefined);
        setSupportedBrands(undefined);
        setSelectedBrand(undefined);
        setSupportedIndices(undefined);
        setSelectedIndex(undefined);
        setNewRemoteMac(undefined);
        setErrorId(undefined);
        init();
    }, [init, setStage]);

    const notificationCallback = useCallback((msg) => {
        if (msg.device_mac !== parentDeviceMac) {
            return;
        }

        switch (msg.sub_event) {
            case "get_sub_device_types":
                setSupportedSubDeviceForDevice(msg.sub_device_types);
                break;
            case "get_remote_brands":
                if (msg.sub_device_type !== selectedSubType) {
                    console.log("Add Remote IGNORE (get_remote_brands)", msg.sub_device_type, selectedSubType);
                    return;
                }
                setSupportedBrands(msg.brands);
                break;
            case "get_remote_indices":
                if ((msg.sub_device_type !== selectedSubType) || (String(msg.brand_id) !== String(selectedBrand))) {
                    console.log("Add Remote IGNORE (get_remote_indices)", msg.sub_device_type, selectedSubType, msg.brand_id, selectedBrand);
                    return;
                }
                setSupportedIndices(msg.indices);
                break;
            case "error":
                setErrorId(msg.error);
                setStage("error")
                break;
            case "virtual_remote_joined_notification":
                if ((msg.sub_device_type !== selectedSubType) || (String(msg.brand_id) !== String(selectedBrand)) || (msg.index !== selectedIndex)) {
                    console.log("Add Remote IGNORE (virtual_remote_joined_notification)", msg.sub_device_type, selectedSubType, msg.brand_id, selectedBrand, msg.index, selectedIndex);
                    return;
                }
                setNewRemoteMac(msg.sub_device_mac);
                break;
            default:
                break;
        }
    }, [parentDeviceMac, selectedSubType, selectedBrand, selectedIndex, setStage]);

    const notificationCallbackId = useState(uuidv4())[0];
    useEffect(() => {
        addRemoteController.registerNotification(notificationCallbackId, notificationCallback);
        return () => {
            addRemoteController.unregisterNotification(notificationCallbackId);
        }
    }, [addRemoteController, notificationCallbackId, notificationCallback]);

    useEffect(() => {
        init();
    }, [init]);

    return (
        <div className="add-remote-page">
            <div className="bg-blank" />
            <div className="bg-secondary-color" />

            <div className="bg-blank" style={{ zIndex: -9, opacity: (stage === "error") ? 1 : 0, transition: "opacity 0.5s linear" }} />
            <div className="bg-failure-color" style={{ zIndex: -8, opacity: (stage === "error") ? 1 : 0, transition: "opacity 0.5s linear" }} />

            <FullPageTransition disableOnFirstTime inverse={inverse}>
                <AddRemoteSelectDeviceType
                    dismiss={dismissHandler}
                    isVisible={stage === "select-type"}
                    onTypeSelected={onTypeSelected}
                    supportedSubDeviceForDevice={supportedSubDeviceForDevice}
                    key="add-remote-page-select-type" />

                <AddRemoteSelectBrand
                    dismiss={() => { back(); }}
                    isVisible={stage === "select-brand"}
                    supportedBrands={supportedBrands}
                    onBrandSelected={onBrandSelected}
                    key="add-remote-page-select-brand" />

                <AddRemoteTestCommand
                    dismiss={() => { back(); }}
                    isVisible={stage === "test-command"}
                    testCommand={testCommand}
                    selectedSubType={selectedSubType}
                    supportedIndices={supportedIndices}
                    onIndexSelected={onIndexSelected}
                    key="add-remote-page-test-command" />

                <AddRemoteCompleted
                    isVisible={stage === "join"}
                    newRemoteName={newRemoteName}
                    renameRemote={renameRemote}
                    cancel={dismissHandler}
                    key="add-remote-page-completed" />

                <AddRemoteError
                    dismiss={dismissHandler}
                    isVisible={stage === "error"}
                    errorId={errorId}
                    onRetry={onRetry}
                    key="add-remote-page-error" />
            </FullPageTransition>
        </div>
    )
}